/**
 * <StickyDecisionCalcBanner /> — bottom-right sticky CTA that floats above
 * the guide body once the reader has scrolled past the hero (≥ 600px).
 *
 * Purpose: convert editorial readers into calculator users *at the
 * moment they're already convinced*. We surface it on the 5 ROI /
 * financing / strategy guides where the next logical action is to run
 * real numbers — not on disaster or how-to guides where the call to
 * action is different.
 *
 * Behavior:
 *   - Hidden until window.scrollY ≥ 600 (so hero + first paragraph land
 *     uninterrupted)
 *   - Dismissible — once closed, sessionStorage flag suppresses for the
 *     rest of the browsing session so it doesn't nag
 *   - Mobile-friendly (compact text + chip layout below sm: breakpoint)
 *   - Tracks impression + click via GA4 dataLayer (matches existing
 *     pageview-event pattern in /lib/analytics.js)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";

const STORAGE_KEY = "hcg.sticky-renovate-or-move.dismissed";

export default function StickyDecisionCalcBanner({
  calcPath = "/renovate-or-move-calculator",
  headline = "Run the 30-second decision math",
  subline = "Renovate or move? Free 5/10/20-yr cost-of-ownership calc",
  ctaLabel = "Run the math",
  testid = "sticky-decision-banner",
  guideSlug,
}) {
  // Initial sessionStorage read happens at state init so we don't set
  // state inside useEffect (lint-clean + avoids a one-frame flash).
  const [dismissed, setDismissed] = useState(() => {
    try {
      return typeof window !== "undefined"
        && sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (dismissed) return undefined;
    const onScroll = () => {
      if (window.scrollY >= 600) setVisible(true);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  // GA4 impression event — fires once when the banner becomes visible
  useEffect(() => {
    if (!visible || dismissed) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "sticky_calc_banner_impression",
        calc_path: calcPath,
        guide_slug: guideSlug || null,
      });
    } catch {
      /* analytics blocked — non-fatal */
    }
  }, [visible, dismissed, calcPath, guideSlug]);

  if (!visible || dismissed) return null;

  const close = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* SS blocked — local state still drops it for this page */
    }
  };

  const onCtaClick = () => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "sticky_calc_banner_click",
        calc_path: calcPath,
        guide_slug: guideSlug || null,
      });
    } catch {
      /* non-fatal */
    }
  };

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-3 z-40 flex justify-center px-3 sm:bottom-4 sm:right-4 sm:left-auto sm:justify-end"
      data-testid={testid}
    >
      <div className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl border-2 border-indigo-600 bg-white/95 p-3 shadow-lg backdrop-blur-sm sm:p-4">
        <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-700 text-2xl sm:inline-flex">
          🔁
        </span>
        <div className="flex-1 leading-tight">
          <p className="font-display text-sm font-semibold text-slate-900 sm:text-base">
            {headline}
          </p>
          <p className="mt-0.5 text-xs text-slate-600 sm:text-sm">{subline}</p>
        </div>
        <Link
          to={calcPath}
          onClick={onCtaClick}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-indigo-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-800 sm:px-4 sm:py-2 sm:text-sm"
          data-testid={`${testid}-cta`}
        >
          {ctaLabel} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <button
          type="button"
          onClick={close}
          aria-label="Dismiss"
          className="absolute right-1 top-1 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 sm:relative sm:right-0 sm:top-0"
          data-testid={`${testid}-dismiss`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * The 5 guide slugs that should surface the sticky banner. Centralized
 * here so adding/removing a guide is a one-line edit.
 *
 * Selection criteria: guides where the reader is researching renovation
 * ROI / financing / sequencing — i.e., already committed to the idea
 * of renovating and looking for execution-level help. The Renovate-or-
 * Move calculator IS that execution help.
 */
export const STICKY_BANNER_GUIDE_SLUGS = new Set([
  "best-roi-renovations-2026-ranked",
  "remodel-vs-refresh-decision-2026",
  "pre-sale-fixes-vs-skip-2026",  // legacy alias, redirects to below
  "best-roi-renovations-before-selling-your-home-2026",
  "how-to-finance-a-home-renovation-2026",
  "home-renovation-order-and-sequence-2026",
  "2026-home-renovation-cost-index-by-state",
  "solar-panel-roi-by-state-2026",
]);

/**
 * Per-guide CTA override. If a slug isn't in this map, the banner defaults
 * to the Renovate-or-Move calculator (the highest-converting decision tool
 * across the renovation/finance content cluster).
 */
export const STICKY_BANNER_CTA_BY_SLUG = {
  "solar-panel-roi-by-state-2026": {
    calcPath: "/should-i-get-solar-calculator",
    headline: "Will solar pay back at your address?",
    subline: "Free state-adjusted Should-I-Get-Solar calculator",
    ctaLabel: "Get my verdict",
  },
};
